import {
  anticipatedItemKind,
  type QuoteIntakeAnticipatedDocument,
} from "@/services/quote-intake-items";
import type { QuoteRequestAttachmentUpload } from "@/services/quoteRequestsService";

/**
 * How many files one RFQ may carry.
 *
 * MUST equal QUOTE_REQUEST_MAX_ATTACHMENTS in
 * apps/backend/src/auth/quote-request.service.ts — the backend suite asserts
 * the two are equal (`pnpm -C apps/backend test:quote-attachment-limit`), so a
 * one-sided change fails there rather than becoming a 400 a consumer discovers
 * after picking twenty files.
 */
export const QUOTE_REQUEST_MAX_ATTACHMENTS = 20;

/**
 * How many anticipated items the intake may put on the screen.
 *
 * A SEPARATE number from the attachment cap, which it used to share. The old
 * `anticipatedDocuments.slice(0, QUOTE_REQUEST_MAX_ATTACHMENTS)` quietly made
 * "how many files may I attach" and "how many things may the conversation ask
 * for" the same quantity; raising the first to 20 would have let the intake
 * render twenty fields, which is a different decision nobody took.
 *
 * Five, because a conversation that asks for more than five things has failed
 * at triage and the fix belongs upstream, not in a longer form.
 */
export const MAX_ANTICIPATED_SLOTS = 5;

/**
 * The slot algebra for the RFQ intake, shared by the form step and the review
 * step (spec b08814a3, D1).
 *
 * Extracted because both screens previously carried their own copy of
 * `setNamedSlot` / `setLooseSlot` / the named-vs-loose partition — byte-for-byte
 * identical, and exactly the drift `AnticipatedItemFields` was created to
 * prevent one level up. Two copies of this logic means an item can behave
 * differently depending on which route the consumer took to reach it.
 *
 * `slotLabel` is a GROUPING label, not a key. It used to be unique by
 * construction — `setNamedSlot` filtered the list by `a.slotLabel !== label`
 * and appended one replacement, which is why a second file replaced the first
 * instead of joining it. Several ATTACHMENTS may now share one label.
 *
 * The label still identifies the SLOT uniquely, and that is a real constraint
 * on the intake rather than a property of this module: two anticipated items
 * with the same label would share one file group here, and one would swallow
 * the other\'s removals. `intakeAnswers` is keyed by label too and has always
 * had the same requirement.
 *
 * The flat array is deliberate and is NOT an accident of history. The create
 * call posts a flat `files[]` multipart alongside an index-aligned
 * `documentTypes` JSON array, and the backend REJECTS a length mismatch rather
 * than zipping the two (`parseIntakeDocumentTypes`). A `Record<label, File[]>`
 * reads better locally and has no defined order, so flattening it back at
 * submit time would reintroduce precisely the misalignment that rejection
 * exists to catch.
 */

/** File-kind items only: a text item answers by typing and owns no attachment. */
export function fileSlotLabels(items: QuoteIntakeAnticipatedDocument[]): Set<string> {
  return new Set(
    items.filter((item) => anticipatedItemKind(item) === "file").map((item) => item.label),
  );
}

/** The attachments sitting in one named slot, in the order they were added. */
export function filesInSlot(
  attachments: QuoteRequestAttachmentUpload[],
  label: string,
): QuoteRequestAttachmentUpload[] {
  return attachments.filter((a) => a.slotLabel === label);
}

/** Everything NOT claimed by a named file slot. */
export function looseAttachmentsOf(
  attachments: QuoteRequestAttachmentUpload[],
  namedLabels: Set<string>,
): QuoteRequestAttachmentUpload[] {
  return attachments.filter((a) => !namedLabels.has(a.slotLabel ?? ""));
}

/**
 * Replace a named slot's files with the given list, stopping at the overall cap.
 *
 * A WHOLE-ARRAY setter rather than an add/remove pair, because that is the
 * contract StyledMultiFileInput has had since it was written: it owns the
 * picking and the per-file removal and hands back the resulting list. Keeping
 * a separate addToSlot/removeFromSlot here would mean translating its one
 * callback into two, which is how two components drift apart.
 *
 * Truncates rather than rejecting the whole pick: a consumer who selects
 * fifteen files with three slots left should keep the three, not lose all
 * fifteen to an error. The caller shows the running count so the ceiling is
 * visible before it is reached.
 *
 * The slot's entries are rebuilt IN PLACE of the old ones rather than appended,
 * so removing one file does not reshuffle the loose attachments that follow —
 * which is what keeps the index-addressed generic slots below pointing at the
 * same files they did before.
 */
export function setSlotFiles(
  attachments: QuoteRequestAttachmentUpload[],
  item: QuoteIntakeAnticipatedDocument,
  files: File[],
): QuoteRequestAttachmentUpload[] {
  const otherCount = attachments.filter((a) => a.slotLabel !== item.label).length;
  const room = Math.max(0, QUOTE_REQUEST_MAX_ATTACHMENTS - otherCount);
  const kept = files.slice(0, room).map((file) => ({
    file,
    documentType: item.documentType,
    slotLabel: item.label,
  }));

  const result: QuoteRequestAttachmentUpload[] = [];
  let inserted = false;
  for (const a of attachments) {
    if (a.slotLabel === item.label) {
      if (!inserted) {
        result.push(...kept);
        inserted = true;
      }
      continue;
    }
    result.push(a);
  }
  if (!inserted) result.push(...kept);
  return result;
}

/**
 * Set or clear one generic (unnamed) slot.
 *
 * Named attachments are re-emitted first so the array stays partitioned, which
 * is what lets the loose slots be addressed by index at all: an index-based
 * lookup over a mixed array would re-point a slot at somebody else's file the
 * moment a named attachment landed.
 *
 * Enforces the cap itself rather than trusting the caller. It previously held
 * only because `quote-request-fields` separately stops rendering the empty
 * slot once `remaining` hits zero — a second, invisible guard in a different
 * file, which is the kind of coupling that survives exactly until someone
 * renders this from a third place.
 *
 * Writing past the end is clamped to an append for the same reason: a bare
 * `loose[index] = …` beyond `length` leaves an `undefined` hole that is spread
 * straight back into the attachment list, and the next read of `a.file.name`
 * — in the multipart build — throws on it.
 */
export function setLooseSlot(
  attachments: QuoteRequestAttachmentUpload[],
  namedLabels: Set<string>,
  index: number,
  file: File | null,
): QuoteRequestAttachmentUpload[] {
  const loose = [...looseAttachmentsOf(attachments, namedLabels)];
  if (file) {
    const replacing = index < loose.length;
    if (!replacing && attachments.length >= QUOTE_REQUEST_MAX_ATTACHMENTS) return attachments;
    const at = Math.min(Math.max(index, 0), loose.length);
    loose[at] = { file, documentType: null, slotLabel: null };
  } else {
    if (index < 0 || index >= loose.length) return attachments;
    loose.splice(index, 1);
  }
  return [...attachments.filter((a) => namedLabels.has(a.slotLabel ?? "")), ...loose];
}
